import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { name: 'Marketing Automation', slug: 'marketing-automation' },
  { name: 'CRM Software', slug: 'crm-software' },
  { name: 'Project Management', slug: 'project-management' },
  { name: 'AI & Machine Learning', slug: 'ai-ml' },
  { name: 'Email Marketing', slug: 'email-marketing' },
  { name: 'HR Management', slug: 'hr-management' },
  { name: 'SEO Tools', slug: 'seo-tools' },
  { name: 'Analytics & BI', slug: 'analytics-bi' },
];

const ToolForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    logo: '',
    description: '',
    category: '',
    categorySlug: '',
    pricing: '',
    priceType: 'freemium',
    rating: 4.5,
    reviews: 0,
    features: [],
    trending: false,
    verified: true,
  });

  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchTool();
    }
  }, [id]);

  const fetchTool = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tools`);
      const tool = response.data.find(t => t.id === id);
      if (tool) {
        setFormData(tool);
      } else {
        setError('Tool not found');
      }
    } catch (error) {
      setError('Failed to fetch tool');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name' && !isEditing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleCategoryChange = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    if (category) {
      setFormData(prev => ({
        ...prev,
        category: category.name,
        categorySlug: category.slug
      }));
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, logo: response.data.url }));
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEditing) {
        await axios.put(`${API}/admin/tools/${id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/tools`, formData, getAuthHeaders());
      }
      navigate('/admin/tools');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save tool');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tools')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Tool' : 'Add New Tool'}
          </h1>
          <p className="text-gray-500">
            {isEditing ? 'Update tool information' : 'Add a new software tool to the directory'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Tool Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., HubSpot"
                required
              />
            </div>
            <div>
              <Label>URL Slug *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="e.g., hubspot"
                required
              />
            </div>
          </div>

          <div className="mt-4">
            <Label>Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of the tool..."
              rows={3}
              required
            />
          </div>

          <div className="mt-4">
            <Label>Logo</Label>
            <div className="flex items-center gap-4 mt-2">
              {formData.logo && (
                <img src={formData.logo} alt="Logo" className="w-16 h-16 rounded-xl object-cover" />
              )}
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button type="button" variant="outline" asChild disabled={uploadingImage}>
                    <span className="cursor-pointer">
                      {uploadingImage ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Upload Image</>
                      )}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-gray-500 mt-1">Or paste an image URL below</p>
              </div>
            </div>
            <Input
              value={formData.logo}
              onChange={(e) => handleChange('logo', e.target.value)}
              placeholder="https://example.com/logo.png"
              className="mt-2"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Category & Pricing</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pricing *</Label>
              <Input
                value={formData.pricing}
                onChange={(e) => handleChange('pricing', e.target.value)}
                placeholder="e.g., Free - $99/mo"
                required
              />
            </div>
            <div>
              <Label>Price Type</Label>
              <Select value={formData.priceType} onValueChange={(v) => handleChange('priceType', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="freemium">Freemium</SelectItem>
                  <SelectItem value="paid">Paid Only</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="custom">Custom Pricing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rating</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={formData.rating}
                onChange={(e) => handleChange('rating', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Features</h2>
          
          <div className="flex gap-2 mb-4">
            <Input
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              placeholder="Add a feature..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
            />
            <Button type="button" variant="outline" onClick={addFeature}>
              Add
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {formData.features.map((feature, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm"
              >
                {feature}
                <button type="button" onClick={() => removeFeature(index)}>
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Verified</Label>
                <p className="text-sm text-gray-500">Mark this tool as verified</p>
              </div>
              <Switch
                checked={formData.verified}
                onCheckedChange={(checked) => handleChange('verified', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Trending</Label>
                <p className="text-sm text-gray-500">Show in trending section</p>
              </div>
              <Switch
                checked={formData.trending}
                onCheckedChange={(checked) => handleChange('trending', checked)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/tools')}>
            Cancel
          </Button>
          <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" disabled={saving}>
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> {isEditing ? 'Update Tool' : 'Create Tool'}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ToolForm;